import * as React from "react";
import { Link } from "react-router-dom";
import { useTestRelated } from "../../hooks/usePracticeData.js";
import { PosterArt, artKindForSlug } from "../../components/FieldArt.js";
import { PracticeSkillCard } from "../../components/PracticeSkillCard.js";

/**
 * Contextual internal links for a test-landing page: the practice topics this
 * test covers (`/practice/:gradeSlug/:topicSlug`) and a small set of games
 * that drill them (`/games/:slug`). Data comes from `useTestRelated`, seeded
 * at prerender time from the same coverage-map join that already powers the
 * reverse direction (practice topics linking to tests) — see
 * `scripts/seo/practice.ts`'s `testRelated`.
 *
 * Renders nothing while loading or when the slug has no coverage-map entry —
 * same "don't render an empty shell" convention as `PracticeTopicRoute`'s
 * game band and `GameDetailRoute`'s related-games section.
 */
export function TestRelatedLinks({
  slug,
  shortName,
}: {
  slug: string;
  shortName: string;
}): React.ReactElement | null {
  const { data } = useTestRelated(slug);
  // il-iar (the cross-canonicalized IAR duplicate) shares /iar's sample report —
  // same special case as its canonical override in scripts/prerender.ts.
  const sampleSlug = slug === "il-iar" ? "iar" : slug;
  if (!data || (data.topics.length === 0 && (data.coveragePages?.length ?? 0) === 0)) return null;

  return (
    <section className="tl-related" aria-labelledby="tl-related-h">
      <div className="container">
        <span className="v-field__sec-eyebrow">Keep going</span>
        <h2 id="tl-related-h" className="v-field__sec-title">
          Practice what's <span className="stripe">on the test.</span>
        </h2>
        <div className="v-practice__siblings">
          <span className="v-practice__tests-label">See what you get</span>
          <div className="v-practice__test-chips">
            <Link to={`/placement/sample/${sampleSlug}`} className="v-practice__test-chip">
              Sample {shortName} placement report →
            </Link>
          </div>
        </div>
        {(data.coveragePages?.length ?? 0) > 0 && (
          <div className="v-practice__siblings">
            <span className="v-practice__tests-label">Browse the official coverage map</span>
            <div className="v-practice__test-chips">
              {data.coveragePages!.map((page) => (
                <Link key={page.path} to={page.path} className="v-practice__test-chip">
                  {page.label}
                </Link>
              ))}
            </div>
          </div>
        )}
        <div className="v-skillcard-grid">
          {data.topics.map((t) => (
            <PracticeSkillCard key={t.slug} skill={t} />
          ))}
        </div>

        {data.games.length > 0 && (
          <div className="tl-related__games">
            {data.games.map((g) => (
              <Link
                className="v-field__poster tl-related__game"
                key={g.slug}
                to={`/games/${g.slug}`}
              >
                <div className="v-field__poster-art">
                  <PosterArt
                    thumbnail={g.thumbnail_url}
                    kind={artKindForSlug(g.slug)}
                    alt={`${g.name} cover art`}
                  />
                </div>
                <div className="v-field__poster-body">
                  <h3 className="v-field__poster-title">{g.name}</h3>
                  {g.tagline && <p className="v-field__poster-desc">{g.tagline}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
